import React, { useMemo, useState } from "react";
import { analyzedPagesLabel, formatPageSpan } from "./scopeDiscoveryApi";
import { formatExcelSourceRanges } from "./rfpUtils";
import RfpScopeDesignPanel from "./RfpScopeDesignPanel";
import RfpProductDecisionPanel from "./RfpProductDecisionPanel";
import { decisionItems, isOpenDecision, itemStillNeedsDecision } from "./productDecision";

const REVIEW_SECTIONS = [
  { key: "support", title: "Support" },
  { key: "training", title: "Training" },
  { key: "implementation", title: "Implementation" },
  { key: "commercial", title: "Commercial" },
  { key: "administrative", title: "Administrative" },
  { key: "administrative_access", title: "Administrative Access" },
];

const CONTEXT_SECTIONS = [
  { key: "existing_environment", title: "Existing environment" },
  { key: "incidental_mentions", title: "Incidental mentions" },
];

function statusMark(status) {
  if (status === "approved") return "✓";
  if (status === "rejected") return "✕";
  if (status === "modified") return "✎";
  return "☐";
}

function itemTitle(item) {
  return item.display_name || item.normalized_name || item.subject || item.rfp_name || "Untitled item";
}

function pageLabel(pages, item) {
  if (item?.source_ranges?.length) return formatExcelSourceRanges(item.source_ranges);
  return formatPageSpan(pages || []).replace(/^Pages /, "") || "—";
}

function NestedGroup({ title, children, testId }) {
  if (!children) return null;
  return (
    <details className="mt-2 rounded border border-slate-200 px-2 py-1 dark:border-slate-700" open data-testid={testId}>
      <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</summary>
      <ul className="mt-1 space-y-1 text-sm text-slate-700 dark:text-slate-200">{children}</ul>
    </details>
  );
}

export default function RfpScopeDiscoveryView({
  jobMeta,
  result,
  loading,
  running,
  error,
  onApprove,
  onReject,
  onSaveEdit,
  onAddItem,
  onSaveDesign,
  onRunDiscovery,
  saving,
  jobId,
  onShowInGrid,
  onProductDecisionApplied,
  requirementRows = null,
}) {
  const scope = result?.scope;
  const pagesLabel = analyzedPagesLabel(jobMeta, result);
  const model = result?.meta?.model || result?.usage?.model || "GPT-5.6 Luna";
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ normalized_name: "", primary_pages: "", classification: "" });
  const [addingSection, setAddingSection] = useState("");
  const [addDraft, setAddDraft] = useState({ normalized_name: "", primary_pages: "", concise_summary: "" });
  const [detailsOpen, setDetailsOpen] = useState(false);

  const primaryTechnologies = useMemo(
    () => (scope?.radware_technologies || []).filter((item) => item.scope_role === "primary"),
    [scope]
  );
  const boundaryItems = scope?.boundary_items || [];
  const openProductDecisions = decisionItems(scope).filter((item) =>
    Array.isArray(requirementRows) ? itemStillNeedsDecision(item, requirementRows) : isOpenDecision(item)
  ).length;

  const counts = useMemo(() => {
    const out = { radware_primary: primaryTechnologies.length, boundary_items: boundaryItems.length };
    for (const section of REVIEW_SECTIONS) {
      out[section.key] = (scope?.[section.key] || []).length;
    }
    return out;
  }, [scope, primaryTechnologies.length, boundaryItems.length]);

  const startEdit = (item) => {
    setEditingId(item.item_id);
    setDraft({
      normalized_name: item.display_name || item.normalized_name || item.subject || "",
      primary_pages: (item.primary_pages || item.pages || []).join(", "),
      classification: item.scope_role || item.classification || item.proposed_classification || "",
      concise_summary: item.summary || item.concise_summary || item.reason_for_ambiguity || item.reason || "",
    });
  };

  const parsePages = (value) =>
    String(value || "")
      .split(/[,\s]+/)
      .map((part) => Number(part))
      .filter((n) => Number.isFinite(n) && n >= 1);

  const renderReviewButtons = (item) => (
    <div className="flex flex-wrap gap-1">
      <button
        type="button"
        disabled={saving}
        className="rounded border border-emerald-300 px-2 py-0.5 text-xs text-emerald-800 disabled:opacity-60 dark:border-emerald-700 dark:text-emerald-200"
        onClick={() => onApprove(item)}
      >
        Approve
      </button>
      <button
        type="button"
        disabled={saving}
        className="rounded border border-rose-300 px-2 py-0.5 text-xs text-rose-700 disabled:opacity-60 dark:border-rose-800 dark:text-rose-300"
        onClick={() => onReject(item)}
      >
        Reject
      </button>
      <button
        type="button"
        className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700 dark:border-slate-600 dark:text-slate-200"
        onClick={() => startEdit(item)}
      >
        Edit
      </button>
    </div>
  );

  const renderEditForm = (item, extraFields = {}) => {
    if (editingId !== item.item_id) return null;
    return (
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <input
          className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950"
          value={draft.normalized_name}
          onChange={(e) => setDraft((prev) => ({ ...prev, normalized_name: e.target.value }))}
          placeholder="Name"
        />
        <input
          className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950"
          value={draft.primary_pages}
          onChange={(e) => setDraft((prev) => ({ ...prev, primary_pages: e.target.value }))}
          placeholder="Pages, e.g. 15, 16, 20"
        />
        <input
          className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950 sm:col-span-2"
          value={draft.concise_summary}
          onChange={(e) => setDraft((prev) => ({ ...prev, concise_summary: e.target.value }))}
          placeholder="Summary"
        />
        <div className="flex gap-2 sm:col-span-2">
          <button
            type="button"
            className="rounded-lg border border-emerald-400 bg-emerald-50 px-3 py-1 text-sm text-emerald-950 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100"
            onClick={() => {
              onSaveEdit(item, {
                normalized_name: draft.normalized_name,
                display_name: draft.normalized_name,
                primary_pages: parsePages(draft.primary_pages),
                pages: parsePages(draft.primary_pages),
                summary: draft.concise_summary,
                concise_summary: draft.concise_summary,
                review_status: "modified",
                ...extraFields,
              });
              setEditingId(null);
            }}
          >
            Save edits
          </button>
          <button type="button" className="text-sm text-slate-500" onClick={() => setEditingId(null)}>
            Cancel
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="rfp-scope-discovery-view">
      <header className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 dark:border-emerald-900 dark:bg-emerald-950/30">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-emerald-950 dark:text-emerald-100">Review RFP Scope</h2>
            <p className="mt-1 text-sm text-emerald-900/80 dark:text-emerald-200/80">
              {openProductDecisions
                ? "Some discoveries need a product decision. The original finding stays on each card."
                : "Items are pre-approved. Modify anything that needs a change, then regenerate Analysis."}
            </p>
            <p className="mt-1 text-sm text-emerald-900/80 dark:text-emerald-200/80">
              Analyzed: {pagesLabel}
              <span className="mx-2">·</span>
              Model: {String(model).replace(/^gpt-/i, "GPT-")}
            </p>
          </div>
          {onRunDiscovery ? (
            <button
              type="button"
              disabled={running || loading}
              data-testid="rfp-discover-scope"
              className="rounded-lg border border-emerald-400 bg-white px-3 py-1.5 text-sm font-medium text-emerald-950 hover:bg-emerald-100 disabled:opacity-60 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100"
              onClick={onRunDiscovery}
            >
              {running || loading ? `Reviewing ${pagesLabel}…` : scope ? "Re-run RFP Scope" : "Run RFP Scope"}
            </button>
          ) : null}
        </div>
        {running || loading ? (
          <p className="mt-2 text-sm text-amber-800 dark:text-amber-200" data-testid="rfp-scope-discovery-loading">
            Reviewing RFP scope from {pagesLabel.toLowerCase()}…
          </p>
        ) : null}
        {error ? (
          <p className="mt-2 text-sm text-rose-700 dark:text-rose-300" data-testid="rfp-scope-discovery-error">
            {error}
          </p>
        ) : null}
      </header>

      {!scope && !running && !loading ? (
        <p className="text-sm text-slate-500">Run RFP Scope to generate a pre-approved review.</p>
      ) : null}

      {scope ? (
        <RfpProductDecisionPanel
          jobId={jobId}
          scope={scope}
          rows={requirementRows}
          onShowInGrid={onShowInGrid}
          onApplied={onProductDecisionApplied}
        />
      ) : null}

      <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900" data-testid="rfp-scope-radware-technologies">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
            Technologies
            <span className="ml-2 font-normal text-slate-400">{counts.radware_primary || 0}</span>
          </h3>
          {scope ? (
            <button
              type="button"
              className="text-xs text-emerald-700 hover:underline dark:text-emerald-300"
              onClick={() => {
                setAddingSection("radware_technologies");
                setAddDraft({ normalized_name: "", primary_pages: "", concise_summary: "" });
              }}
            >
              Add missing item
            </button>
          ) : null}
        </div>
        <div className="space-y-3">
          {primaryTechnologies.map((item) => {
            const pages = item.primary_pages || [];
            return (
              <article
                key={item.item_id}
                data-testid={`rfp-scope-item-${item.item_id}`}
                className="rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">
                      <span className="mr-2 font-mono text-sm">{statusMark(item.review_status)}</span>
                      {itemTitle(item)}
                    </p>
                    <p className="text-xs uppercase tracking-wide text-slate-500">
                      {(item.scope_role || "primary").replace(/_/g, " ")}
                      {item.confidence ? ` · ${item.confidence} confidence` : ""}
                    </p>
                    {(item.rfp_terminology || []).length ? (
                      <p className="text-xs text-slate-500">RFP terminology: {item.rfp_terminology.join("; ")}</p>
                    ) : null}
                  </div>
                  {renderReviewButtons(item)}
                </div>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                  {item.source_ranges?.length ? formatExcelSourceRanges(item.source_ranges) : `Pages ${pageLabel(pages)}`}
                  {(item.supporting_pages || []).length ? ` · Supporting: ${pageLabel(item.supporting_pages)}` : ""}
                  {(item.sizing_pages || []).length ? ` · Sizing: ${pageLabel(item.sizing_pages)}` : ""}
                </p>
                {item.summary || item.concise_summary ? (
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">{item.summary || item.concise_summary}</p>
                ) : null}
                {item.rationale ? <p className="mt-1 text-xs text-slate-500">Rationale: {item.rationale}</p> : null}
                {(item.subtechnologies || []).length ? (
                  <NestedGroup title="Subtechnologies" testId={`rfp-scope-subtechs-${item.item_id}`}>
                    {item.subtechnologies.map((child, index) => (
                      <li key={`${item.item_id}-sub-${index}`}>
                        {child.display_name || child.technology_id || "Untitled"}
                        <span className="text-xs text-slate-500"> — pages {pageLabel(child.pages)}</span>
                      </li>
                    ))}
                  </NestedGroup>
                ) : null}
                {(item.capabilities || []).length ? (
                  <NestedGroup title="Capabilities" testId={`rfp-scope-caps-${item.item_id}`}>
                    {item.capabilities.map((child, index) => (
                      <li key={`${item.item_id}-cap-${index}`}>
                        {child.name}
                        <span className="text-xs text-slate-500"> — pages {pageLabel(child.pages)}</span>
                      </li>
                    ))}
                  </NestedGroup>
                ) : null}
                {(item.integrations || []).length ? (
                  <NestedGroup title="Integrations" testId={`rfp-scope-integrations-${item.item_id}`}>
                    {item.integrations.map((child, index) => (
                      <li key={`${item.item_id}-int-${index}`}>
                        {child.name}
                        <span className="text-xs text-slate-500"> — pages {pageLabel(child.pages)}</span>
                      </li>
                    ))}
                  </NestedGroup>
                ) : null}
                {(item.evidence || []).length ? (
                  <NestedGroup title="Evidence" testId={`rfp-scope-evidence-${item.item_id}`}>
                    {item.evidence.map((child, index) => (
                      <li key={`${item.item_id}-ev-${index}`}>
                        Page {child.page}
                        {child.section ? ` · ${child.section}` : ""}
                        {child.evidence_summary ? ` — ${child.evidence_summary}` : ""}
                      </li>
                    ))}
                  </NestedGroup>
                ) : null}
                {renderEditForm(item)}
              </article>
            );
          })}
          {addingSection === "radware_technologies" ? (
            <div className="rounded-lg border border-dashed border-emerald-300 p-3">
              <input
                className="mb-2 w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950"
                value={addDraft.normalized_name}
                onChange={(e) => setAddDraft((prev) => ({ ...prev, normalized_name: e.target.value }))}
                placeholder="Taxonomy id, e.g. alteon"
              />
              <input
                className="mb-2 w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950"
                value={addDraft.primary_pages}
                onChange={(e) => setAddDraft((prev) => ({ ...prev, primary_pages: e.target.value }))}
                placeholder="Pages"
              />
              <input
                className="mb-2 w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950"
                value={addDraft.concise_summary}
                onChange={(e) => setAddDraft((prev) => ({ ...prev, concise_summary: e.target.value }))}
                placeholder="Summary"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-emerald-400 bg-emerald-50 px-3 py-1 text-sm dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100"
                  onClick={() => {
                    onAddItem("radware_technologies", {
                      technology_id: addDraft.normalized_name,
                      display_name: addDraft.normalized_name,
                      scope_role: "primary",
                      primary_pages: parsePages(addDraft.primary_pages),
                      summary: addDraft.concise_summary,
                    });
                    setAddingSection("");
                  }}
                >
                  Add item
                </button>
                <button type="button" className="text-sm text-slate-500" onClick={() => setAddingSection("")}>
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {result?.design ? (
        <RfpScopeDesignPanel design={result.design} onSaveDesign={onSaveDesign} saving={saving} />
      ) : null}

      {scope ? (
        <section className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-900 dark:bg-amber-950/20" data-testid="rfp-scope-boundary-items">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200">
            Scope beyond selected pages
            <span className="ml-2 font-normal text-amber-700/70 dark:text-amber-300/70">{counts.boundary_items || 0}</span>
          </h3>
          <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
            Incomplete first/last-page sections. These are not Radware technology cards.
          </p>
          <div className="mt-2 space-y-2">
            {boundaryItems.map((item) => (
              <article key={item.item_id || item.subject} className="rounded-lg border border-amber-200 px-3 py-2 text-sm dark:border-amber-900">
                <p className="font-medium text-slate-900 dark:text-slate-100">{item.subject || "Incomplete section"}</p>
                <p className="text-xs text-slate-500">
                  {item.source_ranges?.length ? formatExcelSourceRanges(item.source_ranges) : `Pages ${pageLabel(item.pages)}`}
                </p>
                {item.reason ? <p className="mt-1 text-slate-700 dark:text-slate-200">{item.reason}</p> : null}
              </article>
            ))}
            {!boundaryItems.length ? <p className="text-xs text-slate-500">None detected.</p> : null}
          </div>
        </section>
      ) : null}

      {REVIEW_SECTIONS.map((section) => {
        const items = scope?.[section.key] || [];
        if (!items.length && section.key !== addingSection && !running) {
          if (!scope) return null;
        }
        return (
          <section key={section.key} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                {section.title}
                <span className="ml-2 font-normal text-slate-400">{counts[section.key] || 0}</span>
              </h3>
              {scope && section.key !== "items_requiring_human_validation" ? (
                <button
                  type="button"
                  className="text-xs text-emerald-700 hover:underline dark:text-emerald-300"
                  onClick={() => {
                    setAddingSection(section.key);
                    setAddDraft({ normalized_name: "", primary_pages: "", concise_summary: "" });
                  }}
                >
                  Add missing item
                </button>
              ) : null}
            </div>
            <div className="space-y-3">
              {items.map((item) => (
                <article
                  key={item.item_id}
                  data-testid={`rfp-scope-item-${item.item_id}`}
                  className="rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        <span className="mr-2 font-mono text-sm">{statusMark(item.review_status)}</span>
                        {itemTitle(item)}
                      </p>
                    </div>
                    {renderReviewButtons(item)}
                  </div>
                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                    {item.source_ranges?.length
                      ? formatExcelSourceRanges(item.source_ranges)
                      : `Primary pages: ${pageLabel(item.primary_pages || item.pages)}`}
                  </p>
                  {item.concise_summary ? <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">{item.concise_summary}</p> : null}
                  {item.reason_for_ambiguity ? (
                    <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">{item.reason_for_ambiguity}</p>
                  ) : null}
                  {renderEditForm(item)}
                </article>
              ))}
              {addingSection === section.key ? (
                <div className="rounded-lg border border-dashed border-emerald-300 p-3">
                  <input
                    className="mb-2 w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950"
                    value={addDraft.normalized_name}
                    onChange={(e) => setAddDraft((prev) => ({ ...prev, normalized_name: e.target.value }))}
                    placeholder="Normalized name"
                  />
                  <input
                    className="mb-2 w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-950"
                    value={addDraft.primary_pages}
                    onChange={(e) => setAddDraft((prev) => ({ ...prev, primary_pages: e.target.value }))}
                    placeholder="Pages"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-lg border border-emerald-400 bg-emerald-50 px-3 py-1 text-sm dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100"
                      onClick={() => {
                        onAddItem(section.key, {
                          normalized_name: addDraft.normalized_name,
                          primary_pages: parsePages(addDraft.primary_pages),
                          pages: parsePages(addDraft.primary_pages),
                          concise_summary: addDraft.concise_summary,
                          subject: addDraft.normalized_name,
                        });
                        setAddingSection("");
                      }}
                    >
                      Add item
                    </button>
                    <button type="button" className="text-sm text-slate-500" onClick={() => setAddingSection("")}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        );
      })}

      {scope ? (
        <details className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-300">
          <summary className="cursor-pointer text-xs uppercase tracking-wide text-slate-500">Non-Radware context (not technology cards)</summary>
          {CONTEXT_SECTIONS.map((section) => (
            <div key={section.key} className="mt-2">
              <p className="text-xs font-semibold uppercase text-slate-500">{section.title}</p>
              {(scope[section.key] || []).length ? (
                <ul className="mt-1 list-disc pl-5">
                  {(scope[section.key] || []).map((item) => (
                    <li key={item.item_id || item.normalized_name}>
                      {itemTitle(item)}
                      <span className="text-xs text-slate-400">
                        {" "}
                        — {item.source_ranges?.length ? formatExcelSourceRanges(item.source_ranges) : `pages ${pageLabel(item.pages || item.primary_pages)}`}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-400">None</p>
              )}
            </div>
          ))}
        </details>
      ) : null}

      {result?.usage || result?.meta ? (
        <details
          className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-300"
          open={detailsOpen}
          onToggle={(e) => setDetailsOpen(e.currentTarget.open)}
          data-testid="rfp-scope-discovery-usage"
        >
          <summary className="cursor-pointer text-slate-500">Technical details</summary>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
            <div>
              <dt className="uppercase tracking-wide text-slate-400">Model</dt>
              <dd>{result?.usage?.model || result?.meta?.model || "—"}</dd>
            </div>
            <div>
              <dt className="uppercase tracking-wide text-slate-400">Reasoning</dt>
              <dd>{result?.usage?.reasoning_effort || result?.meta?.reasoning_effort || "—"}</dd>
            </div>
            <div>
              <dt className="uppercase tracking-wide text-slate-400">Scope tokens</dt>
              <dd>{result?.usage?.scope_detection?.total_tokens ?? result?.usage?.total_tokens ?? "—"}</dd>
            </div>
            <div>
              <dt className="uppercase tracking-wide text-slate-400">Design tokens</dt>
              <dd>{result?.usage?.design_extraction?.total_tokens ?? "—"}</dd>
            </div>
            <div>
              <dt className="uppercase tracking-wide text-slate-400">Input tokens</dt>
              <dd>{result?.usage?.input_tokens ?? "—"}</dd>
            </div>
            <div>
              <dt className="uppercase tracking-wide text-slate-400">Comments removed</dt>
              <dd>{result?.meta?.editorial_comment_blocks_removed ?? "—"}</dd>
            </div>
            <div>
              <dt className="uppercase tracking-wide text-slate-400">Taxonomy</dt>
              <dd>{result?.meta?.taxonomy_version ?? "—"}</dd>
            </div>
            <div>
              <dt className="uppercase tracking-wide text-slate-400">Duration</dt>
              <dd>
                {result?.meta?.execution_duration_ms != null
                  ? `${Math.round(result.meta.execution_duration_ms / 1000)}s`
                  : "—"}
              </dd>
            </div>
          </dl>
        </details>
      ) : null}
    </div>
  );
}
